import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { FavouriteOrderItemModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/favouriteOrderItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/favouriteOrderItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/favouriteOrderItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/favouriteOrderItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/favouriteOrderItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/favouriteOrderItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/favouriteOrderItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/favouriteOrderItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/favouriteOrderItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/favouriteOrderItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/favouriteOrderItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/favouriteOrderItem/DELETE_FAILURE'

export type FavouriteOrderItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: FavouriteOrderItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadFavouriteOrderItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().favouriteOrderItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/favourite_order_items.json', options)
      .then(({ data }: { data: FavouriteOrderItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.FAVOURITE_ORDER_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadFavouriteOrderItem(favouriteOrderItemId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.favouriteOrderItems.loadedForKeys]
    const loadedIds = [...state.favouriteOrderItems.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (favouriteOrderItemId && !loadedIds.includes(favouriteOrderItemId)){
      loadedIds.push(favouriteOrderItemId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/favourite_order_items/${favouriteOrderItemId}.json`, options)
      .then(({ data }: { data: FavouriteOrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.FAVOURITE_ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createFavouriteOrderItem(favouriteOrderItem: Partial<FavouriteOrderItemModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      favourite_order_item: favouriteOrderItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/favourite_order_items.json', options, config)
      .then(({ data }: { data: FavouriteOrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.FAVOURITE_ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateFavouriteOrderItem(
  favouriteOrderItem: UpdateParams<FavouriteOrderItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      favourite_order_item: favouriteOrderItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/favourite_order_items/${favouriteOrderItem.id}.json`, options, config)
      .then(({ data }: { data: FavouriteOrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.FAVOURITE_ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteFavouriteOrderItem(favouriteOrderItem: DeleteParams<FavouriteOrderItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/favourite_order_items/${favouriteOrderItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.FAVOURITE_ORDER_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: FavouriteOrderItemModuleState = initialState,
  action: Action = { type: '' },
): FavouriteOrderItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
